import type {
    ImageChooserBlock,
    Money,
    ServerCmsTypesProductSummary as ProductSummary,
} from "@reactivated";

import { fallbackTileImage } from "../../../utils/fallbackTileImage";
import { SKU, skuCatalog } from "../engine/schema";

type ProductStatus = ProductSummary["status"];

/**
 * API rows enrich the merged catalog; schema staging values fill in for
 * SKUs the API didn't return
 */
export type MergedProduct = {
    sku: SKU;
    name: string;
    msrp: Money;
    image: ImageChooserBlock;
    url: string | null;
    status: ProductStatus;
};

/**
 * Lift a hardcoded schema price into the `Money` shape the API serves
 * for production rows. Used only for the schema-fallback path in
 * `buildRow`; arithmetic elsewhere uses `withAmount` to preserve the
 * input Money's currency block.
 */
export const schemaPriceToMoney = (
    amount: number,
    locale: string,
    currency: string,
): Money => ({
    currency: {
        code: currency,
        iso_4217_code: currency,
        sub_unit: 100,
        name: currency,
    },
    amount: amount.toFixed(2),
    decimal_places: 2,
    // Whole-dollar display mirrors the API's serializer (SKS brand convention).
    formatted: new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
    }).format(amount),
});

/**
 * Spread an existing `Money` and override its amount. Used wherever the
 * suite builder produces a Money locally from arithmetic — preserves
 * the source's currency block and `decimal_places` instead of
 * reconstructing them.
 */
export const withAmount = (
    source: Money,
    amount: number,
    locale: string,
): Money => ({
    ...source,
    amount: amount.toFixed(2),
    formatted: new Intl.NumberFormat(locale, {
        style: "currency",
        currency: source.currency.code,
        maximumFractionDigits: 0,
    }).format(amount),
});

const buildRow = (
    sku: SKU,
    apiRow: ProductSummary | undefined,
    locale: string,
    currency: string,
): MergedProduct => ({
    sku,
    name: apiRow?.title ?? skuCatalog[sku].name,
    msrp:
        apiRow?.msrp ??
        schemaPriceToMoney(skuCatalog[sku].price, locale, currency),
    image: apiRow?.image ?? fallbackTileImage,
    url: apiRow?.url ?? null,
    // If it's in the schema but not in the database it's assumed to be active
    status: apiRow?.status ?? "active",
});

/** One entry per SKU in `skuCatalog`: API enriches, schema is fallback. */
export const buildMergedCatalog = (
    apiProducts: ProductSummary[] | null,
    locale: string,
    currency: string,
): Map<SKU, MergedProduct> => {
    const bySku = new Map<string, ProductSummary>();
    if (apiProducts) {
        for (const product of apiProducts) {
            bySku.set(product.sku, product);
        }
    }

    const catalog = new Map<SKU, MergedProduct>();
    for (const sku of Object.keys(skuCatalog) as SKU[]) {
        catalog.set(sku, buildRow(sku, bySku.get(sku), locale, currency));
    }
    return catalog;
};
